import { Component, HostListener, OnDestroy, TemplateRef, ViewChild } from '@angular/core';
import { FileItem, NxFileUploaderComponent } from '@aposin/ng-aquila/file-uploader';
import * as fromInsuredImport from '../../../store/insured-import/insured-import.reducer';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/store';
import { Subject, first, takeUntil } from 'rxjs';
import { ApiState } from 'src/app/model/api-state.model';
import { InsuredImportRecord, InsuredImportRecordError, InsuredImportResponse } from 'src/app/model/insured-import.model';
import { NxDialogService, NxModalRef } from '@aposin/ng-aquila/modal';
import { ModalService } from 'src/app/services/modal.service';
import { FormsService } from 'src/app/services/forms.service';
import { FormControl, FormGroup } from '@angular/forms';

const MAX_FILE_SIZE = 1048576; // 1 MB
const FIRST_INDEX = 0;
const SLICE_LAST_INDEX = -1;

@Component({
  selector: 'app-insured-import',
  templateUrl: './insured-import.component.html',
  styleUrls: ['./insured-import.component.scss'],
})
export class InsuredImportComponent implements OnDestroy {
  @ViewChild('documentUpload', { static: false }) documentUpload!: NxFileUploaderComponent;
  @ViewChild('errorsModal') errorsModal!: TemplateRef<any>;
  @ViewChild('globalErrorsModal') globalErrorsModal!: TemplateRef<any>;

  errorsModalRef?: NxModalRef<any>;
  globalErrorsModalRef?: NxModalRef<any>;

  insuredImportState$ = this.store.select(fromInsuredImport.selectInsuredImportState);
  insuredImportData$ = this.store.select(fromInsuredImport.selectInsuredImportData);

  recordsLength: number | null = null;
  correctRecords: InsuredImportRecord[] | null = null;
  errorRecords: InsuredImportRecord[] | null = null;
  errorGlobal: InsuredImportRecordError[] | null = null;
  errorMessage: string | null = null;
  maxFileSize = MAX_FILE_SIZE;

  ngDestroy$ = new Subject<void>();

  apiState = ApiState;

  readonly insuredImportForm = new FormGroup({
    document: new FormControl([]),
  });

  constructor(
    private store: Store<AppState>,
    private dialogService: NxDialogService,
    private modalService: ModalService,
    private formsService: FormsService,
  ) {
    this.insuredImportState$.pipe(takeUntil(this.ngDestroy$)).subscribe((state) => {
      if (!this.documentUpload?.value) {
        return;
      }

      this.documentUpload.value.forEach((fileItem: FileItem) => {
        switch (state) {
          case ApiState.success:
            fileItem.setUploadedState();
            break;
          case ApiState.error:
            fileItem.setErrorState();
            break;
        }
      });
    });

    this.insuredImportData$.pipe(takeUntil(this.ngDestroy$)).subscribe((data) => {
      if (data) {
        this.recordsLength = data.records?.length || null;
        this.checkForErrors(data);
      }
    });
  }

  @HostListener('window:beforeunload') resetValues() {
    this.ngOnDestroy();
  }

  ngOnDestroy(): void {
    this.ngDestroy$.next();
    this.ngDestroy$.complete();
    this.store.dispatch(fromInsuredImport.actions.reset());
  }

  onFilesAddedChange(files: FileItem[]) {
    if (!this.documentUpload.value || !files) {
      return;
    }

    this.documentUpload.value.forEach((fileItem: FileItem) => {
      if (!fileItem.isUploaded) {
        fileItem.setUploadingState();
        if (fileItem.file) {
          this.store.dispatch(fromInsuredImport.actions.start({ payload: fileItem.file }));
        }
      }
    });
  }

  onFileDelete() {
    this.store.dispatch(fromInsuredImport.actions.reset());
    this.recordsLength = null;
    this.correctRecords = null;
    this.errorRecords = null;
    this.errorMessage = null;
    this.errorGlobal = null;
  }

  importData() {
    this.insuredImportData$.pipe(first()).subscribe((data) => {
      if (data) {
        this.formsService.importInsuredData(data);
        this.modalService.closeModalAll();
        this.store.dispatch(fromInsuredImport.actions.succesfullyImported({ importedToForm: true }));
      }
    });
  }

  checkForErrors(data: InsuredImportResponse) {
    this.errorGlobal = null;
    this.correctRecords = null;
    this.errorRecords = null;
    this.errorMessage = null;
    if (data.records && !('errors' in data)) {
      this.correctRecords = data.records.filter((record) => !record.errors?.length);
      this.errorRecords = data.records.filter((record) => record.errors?.length);
      const firstErrors = this.errorRecords && this.errorRecords[FIRST_INDEX]?.errors;
      if (firstErrors) {
        this.errorMessage = this.getErrorMessage(firstErrors, true);
      }
    } else if (data.errors) {
      this.errorGlobal = data.errors;
      this.errorMessage = data.errors.find((err) => err.message)?.message || null;
      if (this.errorMessage?.endsWith('.')) {
        this.errorMessage = this.errorMessage.slice(FIRST_INDEX, SLICE_LAST_INDEX);
      }
    }
  }

  closeInsuredImportModal() {
    this.modalService.closeModalAll();
  }

  openErrorsModal(): void {
    this.errorsModalRef = this.dialogService.open(this.errorsModal, {
      showCloseIcon: true,
    });
  }

  openGlobalErrorsModal(): void {
    this.globalErrorsModalRef = this.dialogService.open(this.globalErrorsModal, {
      showCloseIcon: true,
    });
  }

  closeErrorsModal(): void {
    this.errorsModalRef?.close();
  }

  closeGlobalErrorsModal(): void {
    this.globalErrorsModalRef?.close();
  }

  getErrorMessage(errors: InsuredImportRecordError[], removeLastDot = false): string {
    return errors
      .map((error, index) =>
        (error.message.endsWith('.') && index !== errors.length + SLICE_LAST_INDEX) ||
        (index === errors.length + SLICE_LAST_INDEX && removeLastDot)
          ? error.message.slice(FIRST_INDEX, SLICE_LAST_INDEX)
          : error.message,
      )
      .join(', ');
  }
}
